import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, Clock, Video, ExternalLink, Users, 
  ChevronRight, Loader2, CalendarDays
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyRegisteredEvents = ({ initialEvents = null }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState(initialEvents || []);
  const [loading, setLoading] = useState(initialEvents === null);

  const fetchMyEvents = useCallback(async () => {
    // Skip fetch if events were provided via props
    if (initialEvents !== null) {
      setEvents(initialEvents);
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/events/user/my-registrations`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data.registrations || []);
      }
    } catch (error) {
      console.error('Error fetching registered events:', error);
    } finally {
      setLoading(false);
    }
  }, [initialEvents]);

  useEffect(() => {
    fetchMyEvents();
  }, [fetchMyEvents]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) > new Date();
  };

  if (loading) {
    return (
      <Card className="border-purple-200">
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
        </CardContent>
      </Card>
    );
  }

  // Don't show section if no events
  if (events.length === 0) {
    return null;
  }

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarDays className="w-5 h-5 text-purple-600" />
            My Upcoming Events
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/my-events')}
            className="text-purple-600 hover:text-purple-700"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {events.slice(0, 3).map((reg) => {
            const event = reg.event || {};
            const upcoming = isUpcoming(event.start_date);
            
            return (
              <div 
                key={reg.id}
                className="bg-white rounded-lg border border-purple-100 p-4 hover:border-purple-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/event/${event.slug}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {event.title || 'Untitled Event'}
                      </h4>
                      {upcoming ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Upcoming</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Past</Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(event.start_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTime(event.start_date)}
                      </span>
                      {event.hosting?.type === 'virtual' && (
                        <span className="flex items-center gap-1 text-purple-600">
                          <Video className="w-3.5 h-3.5" />
                          Virtual
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Join Button for upcoming virtual events with Zoom */}
                  {upcoming && event.zoom_join_url && (
                    <a
                      href={event.zoom_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Join
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {events.length > 3 && (
          <Button 
            variant="outline" 
            className="w-full mt-3 text-purple-600 border-purple-200 hover:bg-purple-50"
            onClick={() => navigate('/my-events')}
          >
            View All ({events.length}) Events
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MyRegisteredEvents;
